import type { Event, EventColor, EventPriority } from "@/lib/types";
import type { Locale } from "@/i18n/locales";

export type TemplateLocale = Locale;

export interface TemplateData {
  slug: string;
  /**
   * Locales this template is published in. A page is created only for these
   * locales: an English-only keyword gets `["en"]`, a Spanish-only one gets
   * `["es"]`, and a genuinely translated pair keeps both. There is no
   * cross-locale fallback — a locale outside this list 404s.
   */
  locales: TemplateLocale[];
  title: string;
  description: string;
  longDescription: string;
  category: string;
  icon?: string; // Icon name for UI display
  requiresPro?: boolean; // Whether this template requires Pro subscription
  relatedLabel?: string; // Short label for Related Templates cards; falls back to title
  events: Omit<Event, "id" | "date">[]; // Events without id/date, will be generated dynamically
  settings?: {
    weekStartsOnSunday?: boolean;
    use12HourFormat?: boolean;
    workingHoursStart?: number;
    workingHoursEnd?: number;
    timeIncrement?: 5 | 10 | 15 | 30 | 60; // Time increment in minutes
    showDates?: boolean;
  };
  seoIntro?: string;
  seoSections?: Array<{
    id: string;
    heading: string;
    paragraphs?: string[];
    bullets?: string[];
  }>;
  faqItems?: Array<{ question: string; answer: string }>;
  howToSteps?: string[];
  relatedTemplateSlugs?: string[];
}

// Extended event options for templates
interface EventOptions {
  icon?: string;
  priority?: EventPriority;
}

// Helper to create consistent event structure
const createEvent = (
  title: string,
  description: string,
  day: number,
  startHour: number,
  startMinute: number,
  endHour: number,
  endMinute: number,
  color: EventColor = "blue",
  options?: EventOptions,
): Omit<Event, "id" | "date"> => ({
  title,
  description,
  day,
  startHour,
  startMinute,
  endHour,
  endMinute,
  color,
  ...options,
});


/**
 * Every template definition. Registration happens through
 * ACTIVE_TEMPLATE_SLUGS below, so a definition that is not listed there is
 * silently unpublished — `templates.test.ts` guards against that.
 */
export const ALL_TEMPLATES: Record<string, TemplateData> = {
  // ============================================
  // PRD Keyword Template 1: Employee Schedule Builder
  // ============================================
  "employee-schedule-builder": {
    slug: "employee-schedule-builder",
    locales: ["en", "es"],
    title: "Employee Schedule Builder",
    relatedLabel: "Employee Schedule Maker",
    description:
      "Employee schedule builder for weekly shift coverage: edit color-coded shifts and share PNG or JPG exports with your team. Free exports have a watermark.",
    longDescription: `Managing employee schedules doesn't have to be complicated. Our Free Employee Schedule Builder helps managers and business owners create, organize, and share work schedules in minutes.

Whether you're running a retail store, restaurant, healthcare facility, or any business with shift workers, this tool makes scheduling simple. Set up morning, afternoon, and night shifts, assign employees to specific time slots, and ensure proper coverage throughout the week.

Key benefits include drag-and-drop scheduling, color-coded shifts for easy visualization, and the ability to export your schedule as an image to share with your team. No more spreadsheet headaches or scheduling conflicts.`,
    category: "Business",
    icon: "Briefcase",
    settings: {
      weekStartsOnSunday: false,
      use12HourFormat: true,
      workingHoursStart: 6,
      workingHoursEnd: 22,
      timeIncrement: 30,
      showDates: false,
    },
    events: [
      // Monday (day 1 when weekStartsOnSunday)
      createEvent("John - Morning", "Front desk", 1, 6, 0, 14, 0, "blue", {
        icon: "Briefcase",
      }),
      createEvent("Sarah - Afternoon", "Front desk", 1, 14, 0, 22, 0, "green", {
        icon: "User",
      }),
      // Tuesday (day 2)
      createEvent("Mike - Morning", "Warehouse", 2, 6, 0, 14, 0, "orange"),
      createEvent("Lisa - Afternoon", "Warehouse", 2, 14, 0, 22, 0, "purple"),
      // Wednesday (day 3)
      createEvent("John - Morning", "Front desk", 3, 6, 0, 14, 0, "blue"),
      createEvent("Mike - Afternoon", "Front desk", 3, 14, 0, 22, 0, "orange"),
      // Thursday (day 4)
      createEvent(
        "Sarah - Morning",
        "Customer Service",
        4,
        6,
        0,
        14,
        0,
        "green",
      ),
      createEvent(
        "Lisa - Afternoon",
        "Customer Service",
        4,
        14,
        0,
        22,
        0,
        "purple",
      ),
      // Friday (day 5)
      createEvent(
        "All Staff Meeting",
        "Conference Room",
        5,
        9,
        0,
        10,
        0,
        "red",
      ),
      createEvent("John - Day Shift", "Front desk", 5, 10, 0, 18, 0, "blue"),
    ],
    seoIntro:
      "A weekly work schedule drawn by hand or kept in a spreadsheet costs you a couple of hours every week, and someone still ends up double-booked or the front counter sits empty at noon. This free employee schedule builder gives you a visual weekly grid where shifts are blocks you can drag, recolor, and rearrange, and the finished week exports as an image you can send to the team — no signup needed.",
    seoSections: [
      {
        id: "why-builder",
        heading: "Why Use a Visual Employee Schedule Builder Instead of a Spreadsheet",
        paragraphs: [
          "Spreadsheets feel familiar, but they push back on every edit. Columns need formatting, cells need merging, and a misclick can easily break a row. On top of that, schedules passed around by email or in shared folders leave everyone guessing which version is current.",
          "With a weekly grid, shifts are just colored blocks. You can see the whole week on one screen, move someone from the morning to the afternoon by dragging, and give each shift type its own color so the layout reads at a glance. When it looks right, export it as an image and send it out.",
          "This works well for small teams that run on shift workers — retail stores, restaurants, clinics, and service businesses. If you coordinate more than a handful of people, a tool like this can turn the weekly routine into a few minutes of work.",
          "Spreadsheets still earn their place on the money side of scheduling: staffing costs, payroll, and reporting sit more comfortably in cells than on a calendar. When those figures start in accounting software, a [QuickBooks Online to Excel converter](https://qbotoexcel.com/) brings them into a spreadsheet you can actually calculate with.",
        ],
      },
      {
        id: "how-to-use",
        heading: "How to Use This Employee Schedule Builder",
        paragraphs: [
          "A good week starts before you touch the grid. Collect availability first, decide how many people each shift needs, and only then assign employees. The builder supports that flow visually: colors and the weekly layout make coverage easy to review, and every change is a quick drag. If you prefer to start from a blank canvas, you can use the [free online schedule builder](/) directly. New to planning shifts? See our step-by-step guide on [how to create a work schedule](/blog/how-to-create-a-work-schedule).",
        ],
        bullets: [
          "Collect availability from the team first, so you know who can work each day.",
          "Confirm coverage needs: how many people are required for each shift and the busiest hours.",
          "Assign employees to shifts and give each shift type its own color.",
          "Review the week for overlaps or empty hours and adjust until coverage looks right.",
          "Publish the week by exporting it as an image and sending it to the team.",
        ],
      },
      {
        id: "plan-shifts",
        heading: "How to Plan Morning, Afternoon, and Night Shifts",
        paragraphs: [
          "Businesses that stay open around the clock — manufacturing, healthcare, hospitality — split the day into three blocks so coverage never stops. The common pattern is a morning shift, an afternoon shift, and a night shift, with a short overlap where crews hand over.",
          "A typical split is morning from 6 AM to 2 PM, afternoon from 2 PM to 10 PM, and night from 10 PM to 6 AM. This template demonstrates the morning and afternoon pattern with sample events between 6 AM and 10 PM. If you run a night crew, plan it the same way on paper first: who rotates into nights, how often, and how the handover lines up. In the editor, give the night shift its own color, and remember that an overnight block has to cross the day boundary, which means splitting it into two blocks in the schedule.",
        ],
        bullets: [
          "Decide whether people stay on a fixed shift or rotate through all three over the month.",
          "Leave a short overlap between crews so handover information does not get lost.",
          "Rotate night shifts fairly and avoid long strings of nights for the same person.",
          "Use one color per shift so the weekly coverage pattern is obvious at a glance.",
        ],
      },
      {
        id: "common-mistakes",
        heading: "Common Employee Scheduling Mistakes to Avoid",
        paragraphs: [
          "Most scheduling problems are process problems, not tool problems. These five habits are behind most weekly scheduling headaches, and a visual weekly grid makes each one easier to catch.",
        ],
        bullets: [
          "Leaving gaps during peak hours. When the busiest part of the day is understaffed, customers wait and staff burn out.",
          "Stacking long or back-to-back shifts on one person. Fatigue often leads to mistakes and turnover.",
          "Skipping the handover buffer. A few minutes of overlap between crews keeps surprises from becoming fires.",
          "Keeping the schedule in one person's head. If it is not shared, changes never reach the team.",
          "Ignoring availability changes. Out-of-date preferences force last-minute swaps every single week.",
        ],
      },
      {
        id: "tool-workflow",
        heading: "How This Employee Schedule Builder Works",
        paragraphs: [
          "Open the template and you get a ready-made week: sample employees, morning and afternoon shifts, and colors already assigned. Replace the sample events with your real staff, then drag blocks to match availability. Need to cover a sudden sick day? Edit the affected shift or add a replacement event for the employee who is out, then export the updated schedule again. Prefer to plan everything from scratch? Open the online schedule builder instead.",
          "The week saves automatically in your browser, so closing the tab does not lose your work. When you want the schedule across your own devices, log in and use Cloud Save — free accounts keep up to 10 templates, and Pro accounts keep up to 50. Exporting to PNG or JPG is free; PDF export is a Pro feature. See the [pricing plans](/pricing) to compare free and Pro limits.",
          "Click Use This Template above to load the schedule builder with this employee schedule pre-filled.",
        ],
      },
    ],
    faqItems: [
      {
        question: "Is this employee schedule builder free to use?",
        answer:
          "Yes, the core schedule builder is free. You can create weekly schedules, customize every shift block, and save them in your browser at no cost. PNG and JPG exports are free too — free accounts include a small 'Created with TrySchedule.com' watermark on exported images, and Pro removes it. Cloud Save is available after signing in: free users can save up to 10 templates, Pro users up to 50. PDF export is a Pro feature.",
      },
      {
        question: "Can I schedule morning, afternoon, and night shifts?",
        answer:
          "You can create blocks within your configured working hours and give each shift its own color. The template comes with sample morning and afternoon shifts between 6 AM and 10 PM. For overnight work, adjust the working hours in settings and split the night across the day boundary into two blocks in the schedule.",
      },
      {
        question: "How do I share the schedule with my team?",
        answer:
          "Export the finished schedule as a PNG or JPG image and send it to your team — free, with no accounts required. Free exports include a small 'Created with TrySchedule.com' watermark, which Pro removes. Cloud Save is your own cloud storage, not a shared team space: sign in to keep and reload up to 10 templates on a free account, or up to 50 with Pro.",
      },
      {
        question: "Is this better than scheduling in Excel?",
        answer:
          "It depends on your situation. For teams that frequently adjust weekly shifts, a visual grid can be easier than a spreadsheet. Excel may still be the better fit for very small teams with stable schedules, or when you need formulas, payroll, or reporting. The trade-off: this is a scheduling grid, not a workforce management system, so you still collect availability and send the final image yourself.",
      },
    ],
    howToSteps: [
      "Load this employee schedule template into the editor",
      "Replace the sample employee names with your actual staff",
      "Drag each person's block to the days and hours they work",
      "Color-code shift types so morning, afternoon, and night are easy to tell apart",
      "Export the week as a PNG or JPG image and send it to the team",
    ],
    relatedTemplateSlugs: ["ai-schedule-builder", "study-schedule-builder"],
  },

  // ============================================
  // PRD Keyword Template 5: AI Schedule Builder
  // ============================================
  "ai-schedule-builder": {
    slug: "ai-schedule-builder",
    locales: ["en", "es"],
    title: "AI Schedule Builder",
    relatedLabel: "AI Schedule Maker",
    description:
      "Describe your week and let an AI schedule generator create an editable plan. Review, adjust, and export it; Pro access and AI Autofill limits apply.",
    longDescription: `Describe your week in plain language and let our AI Schedule Builder turn it into a ready-made weekly plan. Instead of starting with a rigid template or an empty grid, you explain what your week looks like — fixed commitments, time ranges, and priorities — and the tool generates matching schedule blocks you can adjust in the visual editor.

Simply apply this template, and our AI Autofill assistant walks you through the process: describe your typical week, preview the generated events, and apply them to the weekly grid. The result balances deep work, rest, and personal priorities the way you described them — and you can drag, recolor, and resize every block afterward.

Whether you're a busy founder, a student juggling course loads, or anyone who wants a schedule without the setup work, our AI schedule generator takes the heavy lifting out of planning your week.`,
    category: "AI",
    icon: "Sparkles",
    requiresPro: true,
    settings: {
      weekStartsOnSunday: false,
      use12HourFormat: false,
      workingHoursStart: 8,
      workingHoursEnd: 18,
      timeIncrement: 15,
      showDates: true,
    },
    events: [
      // Empty - AI will generate the schedule for you!
    ],
    seoIntro:
      "Starting from an empty calendar is the hardest part of planning your week. This AI schedule builder removes the blank page: describe your week in plain language, and it generates a matching weekly plan you can review, tweak, and export. The AI Autofill assistant requires a signed-in account, and the template itself is a Pro feature.",
    seoSections: [
      {
        id: "what-is-ai",
        heading: "What Is an AI Schedule Builder?",
        paragraphs: [
          "A regular schedule maker asks you to build the week block by block: drag events, set times, pick colors, and repeat until the layout finally works. An AI schedule builder works the other way around — you describe your week in plain language, and it generates the blocks for you. If you prefer to build everything by hand, the [free online schedule builder](/) still works exactly the same as always.",
          "The AI does two things: it creates the events you described, and it infers the right settings for your plan — week start day, 12- or 24-hour time format, and working hours that cover your activities. You can review the result before it touches your calendar, and every generated block is a normal schedule event you can edit later.",
          "AI scheduling helps most when the blank page itself is the obstacle, or when your week changes often and you want to describe a new plan instead of rebuilding it manually. It also helps people who want a structured week but do not want to learn how scheduling tools work.",
        ],
      },
      {
        id: "how-it-works",
        heading: "How AI Schedule Generation Works",
        paragraphs: [
          "The flow is short: open the AI Autofill dialog, describe your week in your own words, and let the assistant generate the events. A preview shows the proposed week grouped by day. When you confirm, the events are applied to the weekly grid and the AI-inferred settings are applied to your calendar.",
          "The AI reads your description the way you wrote it: fixed commitments like work hours or classes anchor the plan, time ranges and durations shape each block, and stated priorities decide what gets the most room. The more specific your description, the closer the first draft matches what you had in mind.",
          "In the preview you can delete individual events you do not want, or go back and edit your description to generate a new version. Dragging, recoloring, and resizing blocks happen after you apply the plan to the weekly grid — the preview is for reviewing, not fine-tuning.",
          "Once applied, the schedule is fully yours: every block behaves like a regular event, so you can drag it to another day, change its length, recolor it, and export the finished week. If you want a completely different plan, the AI quick actions in the editor let you regenerate or start fresh.",
        ],
      },
      {
        id: "use-cases",
        heading: "Who Should Use an AI Schedule Builder?",
        paragraphs: [
          "AI-generated scheduling saves the most time for people whose weeks are complicated or change often — exactly the cases where building a grid by hand feels like a chore. A few of the most common ways people use it:",
        ],
        bullets: [
          "Founders and freelancers: describe deep work, meetings, workouts, and family time in one prompt and get a balanced week without laying it out block by block.",
          "Students: generate a study plan for exam week by describing your subjects, revision rounds, and break preferences.",
          "People juggling work, fitness, and side projects: avoid manually stitching three schedules together by describing all three in one go.",
          "Anyone who wants a schedule but not the setup: describe your typical week, get a draft, and adjust only what needs adjusting.",
        ],
      },
      {
        id: "prompt-tips",
        heading: "How to Describe Your Week for Better AI Results",
        paragraphs: [
          "The quality of the generated schedule depends mostly on the description you give it. These habits produce consistently better first drafts:",
        ],
        bullets: [
          "Start with your fixed commitments — work hours, classes, standing meetings — so the AI builds the rest of the week around them.",
          "Give time ranges or durations instead of vague tasks: \"45 minutes of exercise in the morning\" beats \"work out sometime\".",
          "State priorities or goals for the week, such as \"the priority is delivering project A\", so the AI gives the important work the most space.",
          "Treat the first generation as a draft: if it is off, edit the description and generate again — it is usually faster than rearranging blocks manually.",
        ],
      },
      {
        id: "tool-workflow",
        heading: "How This AI Schedule Builder Works",
        paragraphs: [
          "The template page shows an AI schedule preview canvas with guidance. Pro users who are signed in can click Use This Template to load it into the editor, then open the AI Autofill assistant from there.",
          "AI Autofill requires a signed-in account: free users get a lifetime trial of 10 generations, while Pro users get 1,000 generations per month. Loading this AI template itself requires a Pro subscription — compare the [pricing plans](/pricing) for the details.",
          "Exporting your finished week as a PNG or JPG image is free on every plan. Cloud Save is available on all plans too, with room for 10 templates on free accounts and 50 on Pro. PDF export is a Pro feature.",
          "Your privacy is straightforward: only the description you type and the current schedule settings needed to generate it are sent to our AI provider. Existing calendar events and Google Calendar data are never shared.",
          "Click Use This Template above to load the AI schedule builder and describe your first week. Prefer a manual starting point? Browse the schedule templates instead.",
        ],
      },
    ],
    faqItems: [
      {
        question: "Is the AI schedule builder free to use?",
        answer:
          "The AI Autofill assistant is available to signed-in users on every plan: free accounts get a lifetime trial of 10 generations, and Pro accounts get 1,000 per month. Loading the AI template itself requires a Pro subscription. Exporting schedules as PNG or JPG is free on all plans, Cloud Save is included on every plan (10 templates free, 50 with Pro), and PDF export is a Pro feature.",
      },
      {
        question: "How does the AI know my schedule?",
        answer:
          "It only reads what you tell it: the description you type in the AI Autofill dialog, plus the current schedule settings needed for generation, such as working hours, week start day, and time format. It does not read existing calendar events or Google Calendar data. Your description and settings are sent to our AI provider only to generate the plan.",
      },
      {
        question: "Can I edit the AI-generated schedule?",
        answer:
          "Yes. In the preview you can delete individual events or go back and refine your description to generate a new version. After you apply the plan, every block is a normal event: drag it to another day, resize it, recolor it, and export the week. The AI quick actions in the editor also let you regenerate or start fresh whenever you like.",
      },
      {
        question: "What's the difference from regular templates?",
        answer:
          "Regular templates come with pre-filled sample blocks that you edit by dragging and recolorizing. The AI template starts from your description instead: you type what your week looks like, the AI generates the events and infers the settings, and you review and apply them. After that, editing works the same way in both cases.",
      },
    ],
    howToSteps: [
      "Open the AI Schedule Builder template (Pro subscription required)",
      "Sign in and click AI Autofill, then describe your week in plain language",
      "Preview the generated schedule, remove events you do not want, or edit your description and generate again",
      "Apply the plan to the weekly grid, then drag, recolor, and resize blocks as needed",
      "Export the finished week as a PNG or JPG image, or save it to the cloud after signing in",
    ],
    relatedTemplateSlugs: ["employee-schedule-builder", "study-schedule-builder"],
  },
  "study-schedule-builder": {
    slug: "study-schedule-builder",
    locales: ["en", "es"],
    title: "Free Weekly Study Schedule Maker for Students",
    relatedLabel: "Study Schedule Maker",
    description:
      "Build a weekly study plan for classes, revision, homework, and exam prep. Drag color-coded blocks, save, and export as a PNG or JPG image — no sign-up needed.",
    longDescription:
      "Students at every level face the same challenge: they know what to study, but not when to study it. Too many subjects compete for limited time, and study plans that look good on paper often break after one busy day. This free study schedule maker gives you a visual weekly starting point that turns good intentions into a realistic, adjustable plan.\n\n" +
      "Use this study schedule builder for finals week planning, where you need to allocate extra time to weak subjects without neglecting stronger ones. It also works for building a stable weekly self-study routine with dedicated blocks for homework, reading, and review. If you are preparing for exams like IELTS, CPA, civil service, or graduate exams, the template helps you balance practice tests, reading sessions, and review blocks across the week.\n\n" +
      "After loading the template you can drag and drop events, resize time blocks, rename subjects, and recolor categories with a click. The week starts on Monday with 12-hour time format and 15-minute increments, covering study sessions from early morning through late evening. Your schedule saves automatically in your browser, and you can export it as a PNG or JPG image for free.\n\n" +
      "A visual weekly layout makes it easier to see where your time actually goes. When plans change -- and they will -- you can adjust blocks in seconds instead of starting over. The goal is clarity, consistency, and a study routine that bends without breaking.",
    category: "Education",
    icon: "GraduationCap",
    requiresPro: false,
    events: [
      createEvent("Math Review", "Practice problems and concept review", 1, 9, 0, 10, 30, "blue"),
      createEvent("Science Reading", "Read textbook chapters and take notes", 2, 14, 0, 15, 30, "green"),
      createEvent("Essay Writing", "Work on essays and written assignments", 3, 10, 0, 11, 30, "purple"),
      createEvent("Practice Test", "Timed practice exam session", 4, 9, 0, 10, 0, "orange"),
      createEvent("Flashcard Review", "Quick review of key terms and concepts", 5, 8, 0, 9, 0, "blue"),
      createEvent("Study Break", "Take a break and recharge", 1, 10, 30, 10, 45, "yellow"),
      createEvent("Study Break", "Take a break and recharge", 3, 11, 30, 11, 45, "yellow"),
      createEvent("Group Study", "Collaborative review with classmates", 1, 15, 0, 16, 30, "purple"),
      createEvent("Office Hours", "Visit professor or tutoring session", 3, 14, 0, 15, 0, "green"),
      createEvent("Homework Block", "Complete assigned problem sets", 2, 16, 0, 17, 30, "blue"),
      createEvent("Homework Block", "Complete assigned problem sets", 4, 10, 0, 11, 0, "blue"),
      createEvent("Weekly Planning", "Plan the week ahead and set goals", 1, 8, 0, 8, 30, "yellow"),
    ],
    settings: {
      weekStartsOnSunday: false,
      use12HourFormat: true,
      workingHoursStart: 8,
      workingHoursEnd: 22,
      timeIncrement: 15,
    },
    seoIntro:
      "This weekly template is built for students who want a ready-made starting point instead of an empty calendar. Open it in the editor, customize the pre-filled study blocks, and you will have a clear week of classes, review, and breaks in minutes — no account needed.",
    seoSections: [
      {
        id: "why-use",
        heading: "Why Use a Study Schedule Maker",
        paragraphs: [
          "Students often have more material to cover than free hours in the day. Without a study schedule maker, it is easy to spend too much time on one subject while another gets ignored until the night before an exam. A weekly study schedule shows you exactly where your time is going so you can make deliberate choices about what to prioritize. Prefer to start from a blank week? You can use the [free online schedule builder](/) instead.",
          "A good study planner also reduces decision fatigue. When every session is already mapped out, you spend less energy deciding what to do next and more energy actually learning. This is why students turn to a study schedule builder — it removes the guesswork so you can focus on the material. The visual format helps you spot imbalances — too little time for a weak subject, back-to-back sessions with no break, or a day packed so tightly that one interruption would derail everything.",
          "For students preparing for finals, standardized tests, or professional exams, the structure matters even more. A study timetable turns scattered note-taking into a structured revision plan instead of cramming everything into the last few days. Research shows that distributed practice — spreading review sessions across the week — improves long-term retention significantly compared to cramming everything into one marathon session.",
        ],
      },
      {
        id: "how-to-build",
        heading: "How to Build a Weekly Study Schedule",
        paragraphs: [
          "Building a weekly study schedule starts with a realistic view of your available time. The goal is not to fill every hour but to create a balanced plan you can actually follow. Our study schedule maker gives you a visual weekly canvas where you can drag, resize, and color-code blocks until the layout feels right for your routine. If you need a different starting point, browse the schedule maker templates and pick the one that fits.",
        ],
        bullets: [
          "Start by blocking out your fixed commitments: classes, labs, tutoring sessions, and any part-time work hours. These are the non-negotiable anchors around which your study blocks will fit.",
          "Add study blocks for each subject or exam topic. Give each subject its own color so you can see at a glance how your week is balanced across different areas.",
          "Schedule high-focus work during your most productive hours. If you concentrate best in the morning, place difficult subjects there and save lighter review for the afternoon or evening.",
          "Always leave buffer time between sessions. A 15-minute break between blocks gives your brain a reset and prevents one late start from pushing your entire day off track.",
        ],
      },
      {
        id: "what-to-include",
        heading: "What to Include in a Study Timetable",
        paragraphs: [
          "An effective study timetable covers more than just reading and homework. It should account for the full range of activities that make up a productive student week. Including the right mix of session types keeps your plan realistic and sustainable.",
          "This template already includes sample blocks for review sessions, homework time, practice tests, and study breaks. Most students start by renaming those blocks to match their real subjects and deadlines — no need to build from scratch.",
        ],
        bullets: [
          "Active study sessions for each subject: problem sets for math and science, reading and annotation for humanities, essay drafting for writing-heavy courses.",
          "Review and revision blocks spread throughout the week rather than concentrated on one day — this supports long-term retention far better than last-minute cramming.",
          "Practice test sessions under timed conditions if you are preparing for a standardized exam. Simulating the real test environment builds both knowledge and confidence.",
          "Short breaks between every session to walk around, stretch, or grab a snack. Skipping breaks leads to burnout, and burnout leads to abandoned plans.",
          "A weekly planning slot at the start of each week to adjust your schedule based on upcoming deadlines and test dates.",
        ],
      },
      {
        id: "student-goals",
        heading: "Study Schedule Ideas for Different Student Goals",
        paragraphs: [
          "Every student has a different rhythm, and the best study schedule maker adapts to your specific situation. Here are a few ways students use this weekly template for different goals.",
        ],
        bullets: [
          "Finals week preparation: Build an exam revision schedule that gives extra time to your weakest subjects while keeping lighter review sessions for stronger ones. Add short practice test blocks throughout the week rather than one long session.",
          "Daily self-study routine: Build a consistent after-class schedule with dedicated time for homework, reading, and review. By keeping the same blocks at the same times each day, studying becomes an automatic habit.",
          "Exam prep for IELTS, CPA, or graduate exams: Alternate between knowledge-building sessions, timed practice tests, and error review blocks. The weekly layout helps you track which topics you have covered and which still need attention.",
          "Balancing multiple courses: Assign each course its own color category. When you look at your weekly study schedule, you will immediately see if one class is getting too much attention while another is being neglected.",
        ],
      },
      {
        id: "visual-planner",
        heading: "Why a Visual Study Planner Works Better Than a Blank Calendar",
        paragraphs: [
          "Starting from a blank calendar is intimidating. You have to decide what goes where, how long each block should be, what colors to use, and how to structure your week — all before you have written down a single study session. A visual study planner like this template removes the blank-page problem by giving you a pre-built layout you can adjust.",
          "Once your study schedule is visible as color-coded blocks, it becomes much easier to spot overloaded days, rebalance time across subjects, and protect break time before the week fills up. When exam week approaches, you can quickly see which subjects need more review blocks and adjust in seconds — something a text to-do list cannot show you at a glance. Adjustments that would feel overwhelming in a spreadsheet take only a few seconds with drag-and-drop editing. Need PDF export or cloud saving? Compare the [pricing plans](/pricing).",
        ],
      },
    ],
    faqItems: [
      {
        question: "How do I build a weekly study schedule?",
        answer:
          "Load the Free Study Schedule Maker template, rename the pre-filled blocks to your subjects, adjust the time slots, and save. The template gives you a Monday-start, color-coded week with study sessions, breaks, and review blocks already in place. You can return and edit it anytime in your browser.",
      },
      {
        question: "What should I include in a study timetable?",
        answer:
          "A good study timetable includes active study sessions for each subject, review blocks spread across the week, practice test sessions if you are preparing for an exam, short breaks between every session, and a weekly planning slot to review upcoming deadlines. The goal is to cover all your commitments without packing the schedule so tightly that one disruption throws everything off.",
      },
      {
        question: "Can I save my study schedule and edit it later?",
        answer:
          "Yes — your study schedule is automatically saved in your browser's local storage. You can close the page, come back later, and your schedule will still be there. If you want to access your schedule from multiple devices, cloud sync is available with a TrySchedule Pro subscription.",
      },
      {
        question: "Is this study schedule maker free to use?",
        answer:
          "Yes, the core study schedule maker is completely free. You can create a weekly study plan, customize every event block, save it in your browser, and export it as a PNG or JPG image at no cost. PDF export and cloud sync are available as part of the Pro plan.",
      },
    ],
    howToSteps: [
      "Load the Free Study Schedule Maker template into the editor",
      "Edit subject blocks, adjust time slots, and rename sessions to match your courses",
      "Color-code study categories — use different colors for math, science, reading, and review",
      "Add or resize break blocks between study sessions to keep your plan realistic",
      "Save your schedule in the browser or export it as a PNG or JPG image for sharing or printing",
    ],
    relatedTemplateSlugs: ["ai-schedule-builder", "employee-schedule-builder"],
  },
  // Spanish-only page (keyword "horario de clases" has no English counterpart at
  // this URL). The base entry carries the full Spanish copy on purpose, so
  // TEMPLATE_TRANSLATIONS_ES repeats only the three required fields.
  "horario-de-clases": {
    slug: "horario-de-clases",
    locales: ["es"],
    title: "Horario de Clases: Plantilla Semanal Gratis",
    relatedLabel: "Plantilla de horario de clases",
    description:
      "Arma tu horario de clases en una cuadrícula visual: coloca tus asignaturas, cambia las horas arrastrando y exporta como imagen. Gratis y sin registro.",
    longDescription:
      "Un horario de clases deja de ser un problema cuando puedes cambiarlo en segundos. Esta plantilla ya trae la semana armada, de lunes a viernes, con asignaturas y bloques de hora: solo tienes que cambiar los nombres por los tuyos y mover lo que haga falta.\n\n" +
      "Arrastra un bloque para cambiar la hora de una clase, dale un color a cada asignatura y verás la semana completa de un vistazo. Si un día tienes dos clases seguidas o una que se alarga, el horario se ajusta sin que tengas que rehacer la tabla entera.\n\n" +
      "Cuando ya te sirva, expórtalo como imagen para imprimirlo y colgarlo en el corcho, o mándalo a tu familia por mensaje. Es gratis y no necesitas crear una cuenta para empezar: abres la plantilla, editas y descargas el resultado.",
    category: "Education",
    icon: "GraduationCap",
    requiresPro: false,
    events: [
      createEvent("Matemáticas", "Trae calculadora", 1, 8, 0, 9, 30, "blue"),
      createEvent("Inglés", "Grupo B, aula 4", 1, 10, 0, 11, 30, "purple"),
      createEvent("Historia", "Trabajo en grupo", 1, 12, 0, 13, 30, "orange"),
      createEvent("Lengua y Literatura", "Lectura obligatoria", 2, 8, 0, 9, 30, "green"),
      createEvent("Biología", "Laboratorio", 2, 9, 30, 11, 0, "teal"),
      createEvent("Educación Física", "Pabellón municipal", 2, 12, 0, 13, 30, "red"),
      createEvent("Matemáticas", "Trae calculadora", 3, 8, 0, 9, 30, "blue"),
      createEvent("Arte", "Materiales propios", 3, 10, 0, 12, 0, "pink"),
      createEvent("Lengua y Literatura", "Lectura obligatoria", 4, 8, 0, 9, 30, "green"),
      createEvent("Inglés", "Grupo B, aula 4", 4, 10, 0, 11, 30, "purple"),
      createEvent("Historia", "Trabajo en grupo", 4, 12, 0, 13, 30, "orange"),
      createEvent("Matemáticas", "Repaso del tema 4", 5, 8, 0, 9, 30, "blue"),
      createEvent("Biología", "Laboratorio", 5, 9, 30, 11, 0, "teal"),
      createEvent("Física", "Ejercicios en clase", 5, 11, 30, 13, 0, "yellow"),
      createEvent("Recreo y deporte", "Al aire libre si no llueve", 5, 13, 0, 14, 30, "red"),
    ],
    settings: {
      weekStartsOnSunday: false,
      use12HourFormat: false,
      workingHoursStart: 7,
      workingHoursEnd: 16,
      timeIncrement: 30,
    },
    seoIntro:
      "Cambiar una optativa en octubre no debería obligarte a rehacer media semana a mano. Esta plantilla trae un horario de clases ya montado, de lunes a viernes, con bloques de hora y un color por asignatura: abres el editor, mueves lo que no te sirve y descargas el resultado. Es gratis y no hace falta crear una cuenta.",
    seoSections: [
      {
        id: "que-es",
        heading: "Qué es un horario de clases y para qué sirve",
        paragraphs: [
          "Un horario de clases es una tabla con tres ejes: asignaturas, días de la semana y horas. Sirve para saber qué te toca ahora, en qué aula y con quién, sin abrir el sistema de la institución ni buscar la foto de la pizarra que alguien mandó al grupo. El nivel de detalle cambia según quien lo use: en secundaria suele bastar una semana estable, en la universidad hay que mezclar clases, prácticas y trabajos, y un centro de idiomas reparte alumnos entre grupos distintos cada trimestre.",
          "Puedes montarlo en papel, en una hoja de cálculo o en una app de notas, y funcionar. El problema aparece cuando algo cambia: entra una asignatura nueva, un profesor mueve su clase de hora, dos materias caen en el mismo bloque. En una hoja de cálculo eso significa cortar, pegar y revisar celda por celda. En una cuadrícula visual arrastras el bloque y el resto de la semana se acomoda a la vista, sin fórmulas rotas.",
          "También cambia el momento del curso en que lo miras. En septiembre necesitas la vista completa de la semana; en época de exámenes, lo que quieres es hueco para repasar. Una tabla guardada en PDF no permite ni una cosa ni la otra: toca rehacerla, volver a imprimirla y repartirla otra vez.",
        ],
      },
      {
        id: "como-hacerlo",
        heading: "Cómo hacer tu horario de clases paso a paso",
        paragraphs: [
          "El orden importa. Antes de tocar colores o de pensar en imprimir, decide qué clases son fijas y cuáles pueden moverse. Un horario de clases se construye desde lo que no se puede cambiar hacia lo que sí: si empiezas por lo decorativo, terminas con una semana bonita que no aguanta el primer cambio real.",
        ],
        bullets: [
          "Apunta todas las asignaturas con su carga semanal: cuántas horas de clase tiene cada una. Ese número sale de tu institución, no de tu memoria.",
          "Coloca primero lo inamovible: clases con hora fija, laboratorios, tutorías y optativas que solo se ofrecen una vez.",
          "Resuelve los choques de frente. Si dos clases se solapan, decide cuál cambias de grupo, sin dejarlo para después.",
          "Deja los huecos a la vista. Un bloque libre entre dos clases es tu tiempo para comer, repasar o trasladarte; si lo llenas por inercia, lo pierdes.",
          "Guarda el horario cuando la estructura te convenza, antes de tocar los colores. Si el curso cambia a mitad de trimestre, lo abres de nuevo y mueves solo los bloques afectados.",
          "Si prefieres no partir de una cuadrícula en blanco, abre el [generador de horarios gratuito en línea](/) y trabaja sobre la semana que ya viene armada.",
        ],
      },
      {
        id: "que-incluir",
        heading: "Qué incluir en un horario de clases semanal",
        paragraphs: [
          "Un horario de clases útil responde tres preguntas en cada bloque: qué asignatura, a qué hora y dónde. Todo lo demás es decoración. Si en algún bloque esas tres respuestas no están claras, la tabla te va a fallar justo el día que llegues tarde o que la clase cambie de aula.",
          "Conviene no mezclar dos cosas distintas. Aquí organizas tus clases, es decir, lo que la institución ya decidió por ti. Para decidir cuándo estudiar, repasar o preparar un examen, usa el [planificador de estudio semanal](/templates/study-schedule-builder): allí eliges tú los tiempos y las materias; aquí manda el horario del curso. Para la semana entera, no solo las clases, usa el [horario semanal](/templates/horario-semanal).",
        ],
        bullets: [
          "Los nombres de la asignatura y del profesor o la profesora, tal como aparecen en la lista oficial.",
          "Aula, laboratorio o enlace de videollamada, si la clase es en línea.",
          "Espacio para comer y para desplazarte entre sedes, si tus clases no están todas en el mismo edificio.",
          "Nada de tareas pendientes. Si quieres anotar deberes por asignatura, usa un espacio aparte: la tabla se lee mejor cuando solo dice cuándo y dónde.",
        ],
      },
      {
        id: "errores",
        heading: "Errores comunes al organizar las clases",
        paragraphs: [
          "Casi todos los horarios que se abandonan a las dos semanas fallan por lo mismo. No es la herramienta: es que se armaron copiando o improvisando.",
        ],
        bullets: [
          "Copiar la tabla de un compañero. Los grupos y las optativas no coinciden, y acabas yendo a clases que no son tuyas.",
          "Rellenar cada hueco. Sin pausas ni tiempo de traslado, un retraso de diez minutos desordena la mañana entera.",
          "Elegir colores al azar. Si cada asignatura no tiene su color fijo, dos semanas después ya no sabrás qué estás mirando.",
          "Guardar solo una captura en el móvil. Cuando cambia una clase, tienes que rehacer la imagen desde cero.",
          "Meter las clases y las horas de estudio en la misma tabla. Son dos decisiones distintas y se estorban entre ellas.",
          "Olvidar las clases que no son semanales. Una tutoría cada quince días o una práctica puntual desaparece de la vista hasta que llega el día.",
        ],
      },
      {
        id: "imprimir-compartir",
        heading: "Imprime y comparte tu horario",
        paragraphs: [
          "Aquí está la diferencia con una plantilla de diseño: descargas una imagen ya terminada y, si cambias una clase, vuelves al editor y reordenas todo otra vez. Este horario de clases vive en una cuadrícula editable, así que mover un bloque o renombrar una asignatura cuesta diez segundos, y puedes exportar de nuevo tantas veces como quieras.",
          "Exportar en PNG o JPG es gratis y funciona sin registrarte; si necesitas PDF, mira los [planes de precios](/pricing). Imprime la hoja y pégala donde estudias, o mándala por mensaje a quien te pregunta a qué hora tienes clase. El guardado en la nube está disponible en todos los planes, con sitio para 10 plantillas en la cuenta gratuita y 50 en Pro. Antes de imprimir, revisa la vista previa: los nombres de las asignaturas y las horas tienen que caber sin recortes.",
        ],
      },
    ],
    faqItems: [
      {
        question: "¿Cómo hago un horario de clases si no sé por dónde empezar?",
        answer:
          "No empieces con la cuadrícula en blanco. Abre la plantilla, sustituye los nombres de las asignaturas de ejemplo por los tuyos y arrastra los bloques hasta las horas que te corresponden. En unos minutos tendrás la estructura montada; después puedes afinar colores y detalles. Si te bloqueas, empieza por las clases con hora fija y añade el resto alrededor.",
      },
      {
        question: "¿Puedo editar el horario después de crearlo o tengo que empezar de nuevo?",
        answer:
          "Puedes editarlo siempre que quieras. Cada bloque se mueve, se alarga o cambia de color con un clic, y los nombres de las asignaturas se cambian en cualquier momento. Cuando el cambio esté listo, exportas la imagen otra vez y sustituyes la copia antigua. Eso es justo lo que no puedes hacer con una plantilla descargada en formato de imagen.",
      },
      {
        question: "¿Sirve para un horario de clases de universidad y también de colegio?",
        answer:
          "Sirve para los dos. En el colegio suele funcionar una semana estable, con las mismas asignaturas en los mismos bloques. En la universidad cambia más: hay materias que solo se ven dos días, prácticas en otro edificio y huecos largos entre clases. Usa un color por asignatura y verás esa diferencia de un vistazo.",
      },
      {
        question: "¿Es gratis? ¿Necesito registrarme o instalar algo?",
        answer:
          "Es gratis y funciona en el navegador, sin instalar nada. Puedes montar la semana y exportarla como imagen PNG o JPG sin crear una cuenta; eso sí, las cuentas gratuitas incluyen una pequeña marca de agua 'Creado con TrySchedule.com' en las imágenes exportadas. La exportación a PDF es una función Pro y el guardado en la nube pide iniciar sesión. El plan Pro amplía los límites si necesitas guardar más de 10 plantillas.",
      },
    ],
    howToSteps: [
      "Abre la plantilla de horario de clases y revisa la semana de ejemplo",
      "Cambia los nombres de las asignaturas por los de tu curso",
      "Arrastra los bloques hasta las horas correctas y ajusta su duración",
      "Asigna un color fijo a cada asignatura para leer la semana de un vistazo",
      "Exporta el horario como imagen y compártelo o imprímelo",
    ],
    relatedTemplateSlugs: ["study-schedule-builder", "ai-schedule-builder"],
  },
  // Spanish-only page (keyword "horario semanal"). Like horario-de-clases, the
  // base entry carries the full Spanish copy on purpose, so
  // TEMPLATE_TRANSLATIONS_ES repeats only the three required fields plus the
  // card label and the related slugs.
  //
  // The H1 deliberately omits "Plantilla": the "plantilla de horarios" cluster
  // belongs to /es/templates/plantilla-de-horarios (planned). The template
  // wording is carried by the meta title and description instead.
  "horario-semanal": {
    slug: "horario-semanal",
    locales: ["es"],
    title: "Horario Semanal Gratis y Editable",
    relatedLabel: "Plantilla de horario semanal",
    description:
      "Plantilla de horario semanal lista para usar: coloca tus bloques de horas, cambia lo que no te sirva y descarga la imagen. Gratis y sin crear una cuenta.",
    longDescription:
      "Un horario semanal deja de dar pereza cuando no tienes que construirlo desde cero. Esta plantilla llega con la semana ya montada, de lunes a domingo y con las horas marcadas, así que empiezas por lo que ya tienes delante en lugar de por una cuadrícula vacía.\n\n" +
      "Arrastra un bloque para moverlo de hora, estíralo para que dure más y cambia el nombre por lo que de verdad haces. Cada área tiene su color —trabajo, casa, salud, descanso— y eso hace que la semana se lea de un vistazo, sin buscar nada.\n\n" +
      "Cuando el plan te sirva, exporta la semana como imagen PNG o JPG para imprimirla o mandarla. Y cuando cambie algo, que va a cambiar, vuelves al editor y exportas otra vez.",
    category: "Personal",
    icon: "Home",
    requiresPro: false,
    events: [
      createEvent("Trabajo", "Bloque de concentración", 1, 8, 0, 14, 0, "blue"),
      createEvent("Comida y pausa", "Sin pantallas", 1, 14, 0, 15, 0, "orange"),
      createEvent("Gimnasio", "Fuerza", 1, 19, 0, 20, 30, "red"),
      createEvent("Trabajo", "Bloque de concentración", 2, 8, 0, 14, 0, "blue"),
      createEvent("Curso online", "Unidad 4", 2, 18, 0, 19, 30, "purple"),
      createEvent("Trabajo", "Bloque de concentración", 3, 8, 0, 14, 0, "blue"),
      createEvent("Compra de la semana", "Lista hecha", 3, 17, 30, 18, 30, "yellow"),
      createEvent("Cena con amigos", "Reservado a las 21:00", 3, 20, 30, 22, 30, "pink"),
      createEvent("Trabajo", "Bloque de concentración", 4, 8, 0, 14, 0, "blue"),
      createEvent("Gimnasio", "Cardio suave", 4, 19, 0, 20, 30, "red"),
      createEvent("Trabajo", "Cierre de la semana", 5, 8, 0, 13, 0, "blue"),
      createEvent("Revisión de la semana", "Qué se movió y por qué", 5, 13, 0, 14, 0, "teal"),
      createEvent("Casa", "Limpieza y colada", 6, 10, 0, 12, 0, "orange"),
      createEvent("Familia", "Comida en casa", 6, 13, 0, 16, 0, "pink"),
      createEvent("Paseo", "Sin móvil", 0, 11, 0, 12, 30, "green"),
    ],
    settings: {
      weekStartsOnSunday: false,
      use12HourFormat: false,
      workingHoursStart: 7,
      workingHoursEnd: 23,
      timeIncrement: 30,
    },
    seoIntro:
      "Un horario semanal no falla por falta de disciplina: falla porque a mitad de semana cambia algo y nadie quiere rehacer la tabla. Esta plantilla ya trae la semana montada, de lunes a domingo y con las horas marcadas, así que no empiezas por una cuadrícula vacía. Mueves lo que no te sirva y descargas la imagen para imprimirla. Gratis y sin crear una cuenta.",
    seoSections: [
      {
        id: "que-es",
        heading: "Qué es un horario semanal y para qué sirve",
        paragraphs: [
          "Un horario semanal es una cuadrícula con los días en horizontal y las horas en vertical. Cada bloque que colocas ocupa un tiempo concreto, no una prioridad. Esa es la diferencia con una lista de tareas, y también la razón de que las dos se estorben cuando las juntas en la misma hoja. La lista te dice qué hay que hacer; el plan semanal te dice cuándo cabe. Un martes con ocho pendientes y dos horas libres no se arregla escribiendo mejor la lista: se arregla viendo las dos horas.",
          "Sirve sobre todo para lo que se repite. Levantarse, trabajar, comer, recoger a alguien, entrenar, dormir. Cuando eso está fijo en la tabla, el resto de tu semana se coloca alrededor sin que tengas que decidirlo cada mañana desde cero, y lo que queda libre se ve de un vistazo. Tampoco hace falta usarlo igual todo el año: en septiembre quieres la vista completa, y en un mes cargado te basta con saber que el jueves por la tarde está libre.",
          "Cambia mucho según para quién sea. Trabajo y casa para casi todo el mundo, entrenamiento si preparas una carrera, estudio si tienes exámenes. Por eso esta plantilla arranca con una semana personal, sin asignaturas ni equipos: el horario del curso y la planificación del estudio son otros dos casos y tienen su propia página, enlazada más abajo. Aquí la cuadrícula es tuya y la llenas con lo que tú decidas.",
        ],
      },
      {
        id: "como-hacerlo",
        heading: "Cómo hacer tu horario semanal paso a paso",
        paragraphs: [
          "Hay una tentación que arruina la primera semana: empezar por los colores. Hazlo al revés y te costará veinte minutos en lugar de dos horas. Empieza por lo fijo —el trabajo, la hora a la que comes, el entrenamiento, el rato con quien vive contigo— y deja lo demás para el final. Una cuadrícula bonita no aguanta un miércoles complicado; una realista, sí.",
          "Si prefieres no empezar de cero, abre el [generador de horarios gratuito en línea](/) y trabaja sobre una semana que ya viene armada. Cambia los nombres de los bloques por los tuyos, arrastra lo que esté mal puesto y exporta tu horario semanal cuando te convenza. Se tarda bastante menos en corregir una cuadrícula que se ve que en decidir desde cero cómo repartir los cinco días entre trabajo, casa y descanso.",
        ],
        bullets: [
          "Apunta primero lo que tiene hora fija: el trabajo, la recogida del colegio, el entrenamiento con monitor.",
          "Suma el tiempo de traslado. Es el bloque que casi nadie pone y el que descoloca la mañana entera cuando falta.",
          "Marca los huecos que quieres conservar: comer sin prisa, media hora de lectura, el rato en que no atiendes a nadie.",
          "Cuando dos cosas caigan a la misma hora, decide en ese momento cuál se mueve. Aplazarlo no crea tiempo: solo empuja el problema al día que ya tienes lleno.",
          "Asigna un color por área y no lo cambies a mitad de semana. Si el color baila, la cuadrícula deja de leerse de un vistazo.",
          "Cuando la estructura te encaje, para. Los nombres definitivos y el último color pueden esperar al lunes.",
        ],
      },
      {
        id: "que-incluir",
        heading: "Qué incluir en tu horario semanal",
        paragraphs: [
          "Cada bloque útil responde tres preguntas: qué es, a qué hora y cuánto dura. Si falta la duración, el bloque miente. Poner «estudiar» de 18:00 a 22:00 cuando en realidad estudias cuarenta minutos es la forma más rápida de abandonar el plan en dos semanas. Con las duraciones honestas, la cuadrícula se sostiene sola. Y si un bloque no cabe, casi siempre sobra una cosa, no falta tiempo.",
          "Conviene separar la mañana de la tarde si tu día tiene dos ritmos distintos, porque casi todo el mundo rinde de forma distinta antes y después de comer. Con dos tramos ves enseguida qué queda libre y qué está doblemente cargado. Un horario semanal que mezcla las dos partes en una sola columna larga esconde justo eso: que la tarde ya viene llena. Si tu día empieza de noche, porque estudias por la mañana y trabajas por la tarde, mueve el tramo entero en lugar de pelear con la cuadrícula.",
          "Hay dos casos que se parecen a este y no son este. Si lo que necesitas es el horario fijo de tus clases —asignaturas, aulas y horas que decide tu centro—, eso va en el [horario de clases](/templates/horario-de-clases). Y si lo que quieres es decidir cuándo estudiar, con tus propias sesiones, eso es el [planificador de estudio semanal](/templates/study-schedule-builder). Aquí organizas la semana entera: trabajo, casa, salud y descanso en la misma cuadrícula.",
        ],
      },
      {
        id: "excel-o-editor",
        heading: "Excel, papel o un editor: dónde montar la semana",
        paragraphs: [
          "Las tres opciones funcionan y cada una falla de una manera distinta. En Excel el problema llega con el primer cambio: mueves una fila, se rompen las fórmulas y las celdas combinadas se llevan por delante el resto de la semana. El papel no se rompe, pero un horario semanal impreso no se puede editar: cambiar una cosa de hora significa imprimir otra hoja y tirar la anterior.",
          "El papel tiene otra ventaja que se olvida: se ve sin abrir nada. Una hoja en la pared no compite con cuarenta pestañas abiertas. El problema es que solo aguanta hasta el primer cambio, y una semana cualquiera trae al menos uno: una reunión que se mueve, alguien que se pone malo, una entrega que se adelanta. Si tu plan cambia menos de una vez al mes, papel; si cambia cada semana, cuadrícula editable.",
          "Un editor de cuadrícula está en medio de las dos. No tiene la potencia de una hoja de cálculo ni el encanto de una hoja impresa, pero mover un bloque no rompe nada y volver a exportar cuesta diez segundos. Si tu horario semanal cambia cada semana —y casi siempre cambia—, la diferencia se nota el segundo lunes. Si ya trabajas con Excel, el detalle está en [por qué una hoja de cálculo se queda corta](/blog/schedule-builder-vs-excel); si no, prueba aquí primero.",
        ],
      },
      {
        id: "imprimir-compartir",
        heading: "Imprime la semana y cámbiala cuando haga falta",
        paragraphs: [
          "Exportar es el paso que casi todo el mundo salta y el que decide si el plan sirve. Descarga la imagen en PNG o JPG y ponla donde la vayas a ver sin buscarla: en la nevera, junto al escritorio, dentro de la carpeta que abres cada mañana. Un horario semanal que solo vive en una pestaña del navegador se consulta mucho menos.",
          "Lo que separa esta plantilla de un PDF descargado es lo de después. Cuando cambie algo, y va a cambiar, vuelves al editor, mueves el bloque y exportas otra vez. No hay versión antigua circulando ni hoja impresa que ya no vale, y no tienes que acordarte de dónde guardaste el archivo.",
          "Un repaso rápido antes de gastar papel: si un nombre no cabe, la tabla impresa se corta y no hay forma de arreglarlo después. La exportación en PNG y JPG es gratis y no pide cuenta, pero las imágenes del plan gratuito llevan una marca de agua de TrySchedule. El PDF y la exportación sin marca de agua forman parte de Pro, y el guardado en la nube pide iniciar sesión: 10 plantillas en la cuenta gratuita y 50 en Pro. Si necesitas más, mira los [planes de precios](/pricing).",
        ],
      },
    ],
    faqItems: [
      {
        question:
          "¿Qué es un horario semanal y en qué se diferencia de una lista de tareas?",
        answer:
          "Una tabla con los días en un eje y las horas en el otro. La lista de tareas ordena por importancia; la cuadrícula coloca cada cosa en un momento concreto. Puedes tener las dos, pero en hojas distintas: si mezclas pendientes y horas en la misma tabla, acabas con una lista larguísima donde no se ve cuánto tiempo tienes de verdad.",
      },
      {
        question:
          "¿Cómo organizo la semana si casi todo lo que hago es a la misma hora cada día?",
        answer:
          "Entonces ya tienes medio trabajo hecho. Coloca primero los bloques que se repiten —levantarse, trabajar, comer, dormir— y deja fuera lo que pasa una sola vez. No copies un día en los otros cinco para rellenar: el hueco también es información, sobre todo si quieres meter algo nuevo sin desmontar lo demás.",
      },
      {
        question: "¿Puedo cambiar el horario después de descargarlo?",
        answer:
          "Sí, y es la diferencia principal con un PDF: este horario semanal sigue vivo. Vuelves al editor, mueves el bloque, exportas otra vez y ya está. No hay que rehacer la tabla ni acumular versiones, porque la imagen nueva sustituye a la antigua. Solo necesitas el enlace de la plantilla que guardaste en el navegador.",
      },
      {
        question:
          "¿Es gratis? ¿Hace falta registrarse o instalar algo para hacer un horario semanal?",
        answer:
          "Es gratis y funciona en el navegador, sin instalar nada. Puedes montar la semana y exportarla como PNG o JPG sin crear una cuenta; eso sí, las imágenes del plan gratuito salen con una marca de agua de TrySchedule. La exportación a PDF es una función Pro y el guardado en la nube pide iniciar sesión. El plan Pro también amplía el límite de plantillas guardadas de 10 a 50.",
      },
    ],
    howToSteps: [
      "Abre la plantilla: la semana ya viene montada, de lunes a domingo",
      "Sustituye los bloques de ejemplo por lo que tienes fijo cada semana",
      "Arrastra cada bloque hasta la hora correcta y ajusta su duración",
      "Asigna un color por área —trabajo, casa, salud, descanso— y léelo de un vistazo",
      "Exporta tu horario semanal como imagen PNG o JPG para imprimirlo o compartirlo",
    ],
    relatedTemplateSlugs: ["study-schedule-builder", "horario-de-clases"],
  },
};
const ACTIVE_TEMPLATE_SLUGS = [
  "employee-schedule-builder",
  "ai-schedule-builder",
  "study-schedule-builder",
  "horario-de-clases",
  "horario-semanal",
] as const;

export const TEMPLATES: Record<string, TemplateData> = Object.fromEntries(
  ACTIVE_TEMPLATE_SLUGS.map((slug) => [slug, ALL_TEMPLATES[slug]]),
);

import {
  TEMPLATE_TRANSLATIONS_ES,
} from "./templates-translations";

export function getTemplate(
  slug: string,
  locale: string = "en",
): TemplateData | undefined {
  const template = TEMPLATES[slug];
  if (!template) return undefined;

  // A template is served only in the locales it is published in. There is no
  // English fallback: an unpublished locale must 404, not render foreign
  // content at its own URL.
  if (!template.locales.includes(locale as TemplateLocale)) return undefined;

  if (locale === "es") {
    const translation = TEMPLATE_TRANSLATIONS_ES[slug];
    if (!translation) return undefined;

    const { events: transEvents, ...restTrans } = translation;
    const merged = { ...template, ...restTrans };

    // Merge events if translated events exist
    if (transEvents && template.events) {
      merged.events = template.events.map((evt, i) => {
        const transEvt = transEvents[i];
        return transEvt ? { ...evt, ...transEvt } : evt;
      });
    }
    return merged;
  }

  return template;
}

/**
 * Slugs published in the given locale. Filtering by resolvability (rather than
 * by the `locales` field alone) guarantees this list can never diverge from
 * what `getTemplate` can actually render, so `generateStaticParams` cannot
 * emit a URL that 404s.
 *
 * The locale is required: defaulting it would silently hand back the English
 * list to callers that forgot to pass one.
 */
export function getAllTemplateSlugs(locale: string): string[] {
  return Object.keys(TEMPLATES).filter(
    (slug) => getTemplate(slug, locale) !== undefined,
  );
}

export function getAllTemplates(locale: string): TemplateData[] {
  return getAllTemplateSlugs(locale).map((slug) => getTemplate(slug, locale)!);
}

/**
 * Locales a template is effectively published in.
 *
 * Derived from the declared `locales` intersected with what `getTemplate` can
 * actually render, so metadata/hreflang can never advertise a locale that
 * 404s — e.g. a template that declared "es" but never got a translation.
 */
export function getTemplateLocales(slug: string): TemplateLocale[] {
  const template = TEMPLATES[slug];
  if (!template) return [];

  return template.locales.filter(
    (locale) => getTemplate(slug, locale) !== undefined,
  );
}
